import { t } from "i18next";
import { useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { Ban, CheckCircle2, Eye } from "lucide-react";

import { RowActionsMenu } from "@/core/components/shared/data-table";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useActivatePromotion } from "@/features/fee-engine/api/use-activate-promotion";
import { useDisablePromotion } from "@/features/fee-engine/api/use-disable-promotion";
import type { PromotionRow } from "@/features/fee-engine/components/promotions/promotions-columns";

type PreviewKind = "activate" | "disable";

/** Menu d'actions de ligne pour les promotions (activer / désactiver). */
export function PromotionRowActions({
  promotion,
  navigate: _navigate,
}: {
  promotion: PromotionRow;
  navigate: NavigateFunction;
}) {
  const activate = useActivatePromotion();
  const disable = useDisablePromotion();
  const [preview, setPreview] = useState<PreviewKind | null>(null);
  const pending = activate.isPending || disable.isPending;

  return (
    <>
      <RowActionsMenu
        actions={[
          {
            id: "view",
            label: t("fees.promotions.actions.view"),
            icon: Eye,
            onClick: () => {
              // Pas de page détail dédiée en phase 5 — le coordinateur ajoutera
              // la route si nécessaire (le hook `usePromotion(id)` existe déjà).
            },
          },
          ...(promotion.status !== "ACTIVE"
            ? [
                {
                  id: "activate",
                  label: t("fees.promotions.actions.activate"),
                  icon: CheckCircle2,
                  onClick: () => setPreview("activate"),
                },
              ]
            : []),
          ...(promotion.status === "ACTIVE"
            ? [
                {
                  id: "disable",
                  label: t("fees.promotions.actions.disable"),
                  icon: Ban,
                  variant: "destructive" as const,
                  onClick: () => setPreview("disable"),
                },
              ]
            : []),
        ]}
      />
      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(open) => {
          if (!open) setPreview(null);
        }}
        data={{
          id: promotion.id,
          code: promotion.code,
          status: promotion.status,
          nextStatus: preview === "activate" ? "ACTIVE" : "DISABLED",
        }}
        title={t("common.mutationPreview.statusTitle")}
        description={t("common.mutationPreview.statusDescription")}
        confirmLabel={
          preview === "disable"
            ? t("fees.promotions.actions.disable")
            : t("fees.promotions.actions.activate")
        }
        t={t}
        pending={pending}
        onConfirm={() => {
          if (preview === "activate") {
            activate.mutate(promotion.id, {
              onSuccess: () => {
                notifSilence(t("fees.promotions.feedback.activated"));
                setPreview(null);
              },
              onError: (error) => notifSilenceError(error.message),
            });
            return;
          }
          disable.mutate(promotion.id, {
            onSuccess: () => {
              notifSilence(t("fees.promotions.feedback.disabled"));
              setPreview(null);
            },
            onError: (error) => notifSilenceError(error.message),
          });
        }}
      />
    </>
  );
}
